// src/components/Header.tsx
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useUser, UserButton } from "@clerk/clerk-react";
import { Menu, X } from "lucide-react";
import { useBranding } from '../../contexts/BrandingContext';
import { useRole } from '../../hooks/useRole';
import { switchableBranches } from '../../utils/branches';


const Header = () => {
  const { user } = useUser();
  const location = useLocation();
  const { isClient, isInstructor } = useRole();
  const isSuperAdmin = user?.publicMetadata.role === 'superadmin';
  const isAdmin = user?.publicMetadata.role === 'admin' || isSuperAdmin;
  const branchAccessMode = user?.publicMetadata?.branchAccessMode as string | undefined;
  const branchCodes = Array.isArray(user?.publicMetadata?.branchCodes) ? (user?.publicMetadata?.branchCodes as string[]) : [];
  const { branding, tenantCode } = useBranding();
  const [isBranchMenuOpen, setIsBranchMenuOpen] = useState(false);
  const branchMenuRef = useRef<HTMLDivElement>(null);
  // D4 — el mismo menú en dos formas: fila en escritorio, desplegable en móvil.
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  // Sin tenant (consola superadmin) no hay logo de centro: se muestra la marca
  // Bienesys, que es la del producto.
  const logoSrc = branding?.metadata.logo;
  const logoAlt = branding?.name ? `Logo ${branding.name}` : 'Logo';

  // Construye links con el prefijo del tenant si aplica
  const href = (path: string) => tenantCode ? `/${tenantCode}${path}` : path;
  // El superadmin no tiene /dashboard propio; su home es la consola de tenants
  const homeHref = tenantCode ? href('/dashboard') : (isSuperAdmin ? '/admin/tenants' : '/');
  // P2 — la central entra en la lista aquí: no viene en `branding.branches`
  // (ver `switchableBranches`). El filtro no cambia, así que los modos siguen
  // decidiendo lo mismo: con `subset` la central solo sale si está de verdad en
  // `branchCodes`, que es algo que el back permite conceder.
  const visibleBranches = switchableBranches(branding).filter((branch) => {
    if (branchAccessMode === 'single') {
      return false;
    }

    if (branchAccessMode === 'subset' && branchCodes.length > 0) {
      return branchCodes.includes(branch.code);
    }

    return true;
  });

  // D4 — una sola lista de enlaces para las dos formas del menú. Antes estaban
  // escritos a mano en el JSX; duplicarlos para móvil habría garantizado que se
  // desincronizaran a la primera pantalla nueva.
  const navLinks: { to: string; label: string }[] = isSuperAdmin
    ? [{ to: '/admin/tenants', label: 'Admin' }]
    : isClient
      ? [{ to: href('/mi-calendario'), label: 'Mi Calendario' }]
      // P6 fase 2 — un instructor solo tiene su propia pantalla. Los enlaces de
      // operador le rebotan a /mis-clases desde `ProtectedOperatorRoute`, así que
      // ofrecérselos era prometerle pantallas a las que no puede entrar.
      : isInstructor
        ? [{ to: href('/mis-clases'), label: 'Calendario' }]
        : [
          { to: href('/clientes'), label: 'Clientes' },
          { to: href('/calendario'), label: 'Calendario' },
          // Usuarios, Auditoría y Configuraciones exigen ADMIN en la API:
          // mostrarlos a un operador solo lleva a un 403.
          ...(isAdmin ? [
            { to: href('/usuarios'), label: 'Usuarios' },
            { to: href('/auditoria'), label: 'Auditoría' },
            { to: href('/configuraciones'), label: 'Configuraciones' },
          ] : []),
        ];

  const canSwitchBranches = !isSuperAdmin && (branchAccessMode === 'all' || branchAccessMode === 'subset') && visibleBranches.length > 1;
  const currentBranchLabel = branding?.name ?? 'Sucursal';

  useEffect(() => {
    setIsBranchMenuOpen(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname, tenantCode]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (branchMenuRef.current && !branchMenuRef.current.contains(event.target as Node)) {
        setIsBranchMenuOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    // inset-x-0 (not w-screen) so the fixed header never overflows horizontally on mobile
    <header className="text-white p-2 flex shadow-md justify-between fixed items-center inset-x-0 top-0 z-10" style={{ backgroundColor: 'var(--brand-primary)' }}>
      {/* Logo a la izquierda */}
      <div className="text-sm font-bold ml-2 sm:ml-6 min-w-0">
        <div className="flex items-center gap-3">
          <Link to={homeHref} className="flex h-12 items-center gap-2.5">
              {logoSrc
                ? <img src={logoSrc} alt={logoAlt} className="h-12 w-auto" />
                : <>
                    {/* Isotipo sobre pastilla blanca, igual que el app icon de la
                        guía. El lockup completo no vale aquí: su texto es azul
                        marino sobre una cabecera azul marino, y el lazo superior
                        del símbolo también se perdería contra el fondo. */}
                    <img
                      src="/bienesys-icon.png"
                      alt=""
                      aria-hidden="true"
                      className="h-9 w-9 shrink-0 rounded-lg bg-white p-1"
                    />
                    <span className="font-heading text-base font-semibold tracking-wide text-white">
                      Bienesys
                    </span>
                  </>}
          </Link>
          {canSwitchBranches ? (
            <div className="relative" ref={branchMenuRef}>
              <button
                type="button"
                onClick={() => setIsBranchMenuOpen((prev) => !prev)}
                className="rounded bg-white/10 px-3 py-2 text-xs font-semibold text-white"
              >
                {currentBranchLabel}
              </button>
              {isBranchMenuOpen && (
                <div className="absolute left-0 top-full z-20 mt-2 min-w-56 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
                  <div className="border-b border-gray-100 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                    Cambiar sucursal
                  </div>
                  <div className="max-h-72 overflow-auto py-1">
                    {visibleBranches.map((branch) => (
                      <Link
                        key={branch.code}
                        to={`/${branch.code}/dashboard`}
                        onClick={() => setIsBranchMenuOpen(false)}
                        className={`block px-3 py-2 text-sm hover:bg-gray-100 ${branch.code === tenantCode ? 'bg-gray-100 font-semibold text-gray-900' : 'text-gray-700'}`}
                      >
                        <div className="flex items-center gap-2">
                          <span>{branch.name}</span>
                          {/* Sin esto la central es un nombre más de la lista y no
                              hay forma de saber cuál es el centro principal. */}
                          {branch.isRoot && (
                            <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                              Central
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-400">{branch.code}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
      
      {/* D4 — menú. En móvil no cabe una fila de cinco enlaces (se salía 184px
          a 375px, tapando el logo), así que por debajo de `md` se pliega en un
          desplegable y aquí solo queda el botón. */}
      <nav className="hidden md:flex items-center justify-center flex-1">
        <div className="flex gap-6">
          {navLinks.map(({ to, label }) => (
            <Link key={to} to={to} className="hover:text-gray-300 text-white font-bold">
              {label}
            </Link>
          ))}
        </div>
      </nav>
      
      {/* Menú plegado (solo móvil) */}
      <div className="md:hidden relative ml-auto mr-2" ref={mobileMenuRef}>
        <button
          type="button"
          onClick={() => setIsMobileMenuOpen((open) => !open)}
          aria-label="Menú"
          aria-expanded={isMobileMenuOpen}
          className="p-2 rounded hover:bg-white/10"
        >
          {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
        {isMobileMenuOpen && (
          <div className="absolute right-0 top-full z-30 mt-2 min-w-48 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
            {navLinks.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className="block px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                {label}
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Perfil de usuario a la derecha */}
      <div className="flex items-center gap-2 mr-2 sm:mr-6 shrink-0">
        <span><UserButton/></span>
        {/* El nombre se oculta en móvil para dejar sitio al menú */}
        <span className="hidden sm:inline text-sm font-bold text-center">{user?.firstName || user?.username}</span>
      </div>
    </header>
  );
};

export default Header;
